/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable unicorn/no-zero-fractions */
/* eslint-disable unicorn/numeric-separators-style */
import { useMediaQuery } from '@chakra-ui/react';
import { ReactGrid, Id, DropPosition } from '@silevis/reactgrid';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CHART_COLORS, getCurrencySymbol, HEADER_HEIGHT_WITH_NAVIGATION } from 'projectConstants';
import {
  FetchPlangPageIndicatorTableData,
  UpdatePlangPageIndicatorTableData,
  filterEndDate,
  filterStartDate,
  getShowNonEditableColumns,
  getBlockDetails,
  getIndicatorTableData,
  getModelDetails,
  // getSelectedDriver,
  getmultiDimFiltersItems,
  planPageShow,
  toggleShowNonEditableColumns,
  updateIndicatorData,
} from 'redux/PlanPageSlice';
import {
  FetchBlockOutputs,
  getPlannerBlock,
  getPlannerModel,
  getBaseScenario as getBaseScenarioFromPlannerModeSlice,
} from 'redux/PlannerModeSlice';
import { getBaseScenario as getBaseScenarioFromScenarioSlice } from 'redux/ScenarioSlice';
// import { fetchIndicatorDetail } from 'redux/IndicatorPageSlice';

import { HeaderRow } from './HeaderRow';
import { TextCellTemplate } from './TextCellTemplate';

import './Table.scss';

interface FilterItem {
  name: string;
  status: boolean;
  search: boolean;
}

interface FilteredItem {
  dimId: string | number;
  filterItems: FilterItem[];
}

interface TableObject {
  [key: string]: {
    value: string;
    id?: string | number;
  };
}

interface ProcessedFilterItem {
  dimensionId: string;
  filterItems: string[];
}

interface ColumnHeader {
  id: string | number;
  name: string;
  is_dimension: boolean;
}

interface TableData {
  column_headers: ColumnHeader[];
  excel_data: Array<Record<string, any>>;
  dimensions: Array<{ id: string | number; name: string }>;
  type: string;
}

interface Column {
  columnId: string;
  name: string;
  width: number;
  resizable: boolean;
  reorderable: boolean;
  timeDimId?: string | number;
}

interface Cell {
  type: 'header' | 'text';
  colType?: 'header';
  nonEditable?: boolean;
  property?: string;
  id?: string | number;
  text: string;
  className?: string;
  data?: any;
  rowId?: string | number;
  currencyType?: string;
  uniqueId?: number;
  format?: string;
  color?: string;
  rows?: number;
  itemmId?: string;
  index?: number;
  columnId?: string;
}

interface Row {
  rowId: string;
  height: number;
  reorderable?: boolean;
  cells: Cell[];
}

interface TableItem {
  id: string;
  [key: string]: any;
}

interface CellChange {
  rowId: string;
  columnId: string;
  newCell: {
    text: string;
    [key: string]: any;
  };
}

interface UpdatePayload {
  indicator_id: string;
  request_body: {
    bulk_input: boolean;
    format_excel_data: string;
    dimensions: Array<{ id: string | number; name: string }>;
    data_values: Array<Record<string, any>>;
    type: string;
    scenario_id: string | number;
  };
}

interface Dimension {
  id: string | number;
  name: string;
}

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const isHeaderNonEditable = (data: ColumnHeader, date?: string): boolean => {
  if (data.is_dimension) return true;
  if (!date) return false;

  const isMonthlyNonEditable = (dataHeading: string): boolean => {
    const headingParts = dataHeading.split('-');
    const scenarioParts = date.split('-');
    if (headingParts.length < 2 || scenarioParts.length < 2) return false;

    const headingYear = Number(headingParts[1]);
    const scenarioYear = Number(scenarioParts[1]);
    if (!Number.isFinite(headingYear) || !Number.isFinite(scenarioYear)) return false;

    if (headingYear < scenarioYear) return true;
    return (
      headingYear === scenarioYear &&
      monthNames.indexOf(headingParts[0] as typeof monthNames[number]) <=
        monthNames.indexOf(scenarioParts[0] as typeof monthNames[number])
    );
  };

  if (data.name.startsWith('FY')) {
    const scenarioYear = Number(date);
    if (!Number.isFinite(scenarioYear)) return false;
    return Number(data.name.slice(2, 4)) + 2000 < scenarioYear;
  }

  return isMonthlyNonEditable(data.name);
};

const createHeaderRow = (tableProperty: ColumnHeader[]): Row => ({
  rowId: 'header',
  height: 40,
  cells: tableProperty.map((data, index) => ({
    type: 'header',
    colType: 'header',
    nonEditable: true,
    property: data.is_dimension ? 'dimension' : '',
    id: data.id,
    text: data.name,
    className: `header-cell ${index === tableProperty.length - 1 ? 'header-last' : ''} ${
      index === 0 ? 'header-first' : ''
    }`,
    data,
  })),
});

const createTableRows = (
  tableItems: TableItem[],
  tableHeaders: ColumnHeader[],
  isPercentage: string,
  modelCurrency?: string,
  date?: string,
): Row[] => {
  if (!tableItems?.length || !tableHeaders?.length) return [];

  const isCellNonEditable = (data: ColumnHeader): boolean => isHeaderNonEditable(data, date);

  const headerRow = createHeaderRow(tableHeaders);
  const dataRows = tableItems.map((item, index) => ({
    rowId: String(item.id),
    height: 40,
    reorderable: true,
    cells: tableHeaders.map((data, childIndex) => {
      const fData = data.is_dimension
        ? item[String(data.id)]
        : isPercentage === 'percentage'
        ? item[data.name] * 100
        : item[data.name];

      return {
        type: 'text' as const,
        currencyType: modelCurrency,
        property: data.is_dimension ? 'dimension' : '',
        uniqueId: fData?.uniqueid,
        format: isPercentage,
        color: CHART_COLORS[fData?.uniqueid],
        rows: tableItems.length,
        itemmId: data.is_dimension ? `${fData?.id}` : `${data.id}`,
        text: data.is_dimension ? `${fData?.value}` : `${fData}`,
        index,
        data,
        columnId: data.name,
        rowId: data.is_dimension ? String(data.id) : data.name,
        nonEditable: isCellNonEditable(data),
        className: [
          tableItems.length - 1 === index && !data.is_dimension ? 'border-right' : '',
          tableItems.length - 1 === index && childIndex === 0 ? 'border-bottom' : '',
          !data.is_dimension && isCellNonEditable(data) ? 'non-edit-bg' : '',
          data.is_dimension ? 'dimension-cell' : '',
        ]
          .filter(Boolean)
          .join(' '),
      };
    }),
  }));

  return [headerRow, ...dataRows];
};

const calculateColumnWidth = (
  totalElements: number,
  totalWidth: number,
  isConstantByYear: boolean,
): number => {
  const NAME_FIELD_WIDTH = 150;
  const EDIT_FIELD_WIDTH = 60;
  const MIN_COLUMN_WIDTH = isConstantByYear ? 120 : 100;
  const MAX_COLUMN_WIDTH = isConstantByYear ? 800 : 600;
  const PADDING = 20;

  if (isConstantByYear) {
    const availableWidth = totalWidth - NAME_FIELD_WIDTH - EDIT_FIELD_WIDTH - PADDING;
    const baseWidth = Math.min(
      MAX_COLUMN_WIDTH,
      Math.max(MIN_COLUMN_WIDTH, availableWidth / Math.min(totalElements, 12)),
    );
    return Math.floor(baseWidth);
  }

  return Math.min(
    MAX_COLUMN_WIDTH,
    Math.max(MIN_COLUMN_WIDTH, (totalWidth - EDIT_FIELD_WIDTH) / (totalElements + 1)),
  );
};

interface Properties {
  refetchRequest: () => void;
  isPercentage: any;
  isBuilderMode?: boolean;
}

// Move extractYear function outside component
const extractYear = (dateString: string, modelDetails?: any) => {
  if (!modelDetails?.time_properties) return dateString;

  const modelStartYear = moment(modelDetails.time_properties.starting_period).year() % 1000;

  if (modelDetails.time_properties.time_granularity.toLowerCase() === 'm') {
    const parsedDate = moment(dateString, 'MMM-YY');
    const extractedYear = parsedDate.year() % 1000;

    const fiscalMonthIndex = monthNames.indexOf(modelDetails.time_properties.fy_start_month);
    const modelMonthIndex = moment(modelDetails.time_properties.starting_period).month();
    if (extractedYear === modelStartYear && modelMonthIndex >= fiscalMonthIndex) {
      return `FY${extractedYear + 1}`;
    }
    return `FY${extractedYear}`;
  }
  return `FY${Number(dateString) % 1000}`;
};

// Move the hook outside the component
const useTimeFilterIndices = (
  tableData: TableData | null,
  startDate: string,
  endDate: string,
  type: string,
  modelDetails?: any,
) => {
  return useMemo(() => {
    if (!tableData?.column_headers?.length) return null;

    const startDateValue =
      type === 'constant_by_year' ? extractYear(startDate, modelDetails) : startDate;
    const endDateValue = type === 'constant_by_year' ? extractYear(endDate, modelDetails) : endDate;

    const columnStartIndex = tableData.column_headers.findIndex(
      (cell) => String(cell.name) === String(startDateValue),
    );
    const columnEndIndex = tableData.column_headers.findIndex(
      (cell) => String(cell.name) === String(endDateValue),
    );

    if (columnStartIndex === -1 || columnEndIndex === -1) return null;

    return {
      columnStartIndex,
      columnEndIndex,
      activeDim: tableData.dimensions?.length || 0,
    };
  }, [tableData?.column_headers, startDate, endDate, type, modelDetails]);
};

// Update interfaces for better type safety
interface GridProperties {
  rows: Row[];
  columns: Column[];
  onCellsChanged: (changes: any) => void;
  onColumnResized: (columnId: Id, width: number) => void;
  onColumnsReordered: (targetColumnId: Id, columnIds: Id[], dropPosition: DropPosition) => void;
  stickyLeftColumns: number;
  stickyTopRows: number;
}

// Memoize grid props to prevent unnecessary re-renders
const useGridProperties = (
  rows: Row[],
  columns: Column[],
  onCellsChanged: (changes: CellChange[]) => void,
  handleColumnResize: (columnId: Id, width: number) => void,
  handleColumnsReorder: (targetColumnId: Id, columnIds: Id[], dropPosition: DropPosition) => void,
  currentType: string,
  dimensionsLength: number,
): GridProperties => {
  return useMemo(
    () => ({
      rows,
      columns,
      onCellsChanged,
      onColumnResized: handleColumnResize,
      onColumnsReordered: handleColumnsReorder,
      stickyLeftColumns: currentType === 'constant' ? 0 : dimensionsLength,
      stickyTopRows: 1,
    }),
    [
      rows,
      columns,
      onCellsChanged,
      handleColumnResize,
      handleColumnsReorder,
      currentType,
      dimensionsLength,
    ],
  );
};

// Memoize cell templates
const useCellTemplates = () => {
  const headerRowTemplate = useCallback(() => new HeaderRow(), []);
  const textCellTemplate = useCallback(() => new TextCellTemplate(), []);

  return useMemo(
    () => ({
      header: headerRowTemplate(),
      text: textCellTemplate(),
    }),
    [headerRowTemplate, textCellTemplate],
  );
};

// Optimize table container styles
const useTableStyles = (
  isBuilderMode: boolean,
  isSmallDevice: boolean,
  isMobile: boolean,
  rowsLength: number,
  columnsLength: number,
) => {
  return useMemo(
    () => ({
      maxH: isBuilderMode
        ? `calc(95vh - ${HEADER_HEIGHT_WITH_NAVIGATION} - 35vh)`
        : isSmallDevice
        ? `calc(88vh - ${HEADER_HEIGHT_WITH_NAVIGATION} - 35vh)`
        : isMobile
        ? `calc(92vh - ${HEADER_HEIGHT_WITH_NAVIGATION} - 35vh)`
        : `calc(95vh - ${HEADER_HEIGHT_WITH_NAVIGATION} - 35vh)`,
      overflow: rowsLength >= 2 ? 'auto' : 'none',
      paddingRight: rowsLength > 3 && columnsLength > 3 ? '0' : '1.3rem',
    }),
    [isBuilderMode, isSmallDevice, isMobile, rowsLength, columnsLength],
  );
};

const MultiDimensionTableOpz = ({
  refetchRequest,
  isPercentage,
  isBuilderMode = false,
}: Properties) => {
  const tableReference = useRef<HTMLDivElement>(null);
  const { blockId } = useParams();
  const dispatch: any = useDispatch();
  const indicatorTableData = useSelector(getIndicatorTableData);
  // const selectedBlockDimension = useSelector(getSelectedBlockDimension);

  // Use PlannerModeSlice when in builderMode, otherwise use ScenarioSlice
  const baseScenarioFromPlannerMode = useSelector(getBaseScenarioFromPlannerModeSlice);
  const baseScenarioFromScenario = useSelector(getBaseScenarioFromScenarioSlice);
  const baseScenario = isBuilderMode ? baseScenarioFromPlannerMode : baseScenarioFromScenario;
  const dimensionList = useSelector(getBlockDetails);
  const show = useSelector(planPageShow);
  const [render, setRender] = useState(false);
  const timeDimension = dimensionList?.dimensions.find(
    (item: any) => item?.name?.toLowerCase() === 'time',
  );
  const modelData = useSelector(getModelDetails);
  const plannerModelData = useSelector(getPlannerModel);
  const modelDetails = modelData || plannerModelData;
  const plannerBlock = useSelector(getPlannerBlock);
  const currentType = indicatorTableData?.type;
  const [isMobile] = useMediaQuery('(max-height: 780px)');
  const [isSmallDevice] = useMediaQuery('(max-height: 680px)');
  const [tableData, setTableData] = useState<any>('');
  const filteredItems = useSelector(getmultiDimFiltersItems);
  const startDateFilter = useSelector(filterStartDate);
  const endDateFilter = useSelector(filterEndDate);
  // const [globalRows, setTableDataRows] = useState<any>([]);
  const startDateObject = new Date(baseScenario?.start_date);
  startDateObject.setMonth(startDateObject.getMonth() - 1);
  // Format 1: Year 2023
  const yearFormat = startDateObject.getFullYear();

  // Format 3: Nov-23
  const monthFormat = `${monthNames[startDateObject.getMonth()]}-${String(yearFormat).slice(-2)}`;

  const targetDate = () => {
    if (indicatorTableData?.type === 'raw') {
      return monthFormat;
    }
    return String(yearFormat);
  };

  // Get memoized indices at component level
  const timeFilterIndices = useTimeFilterIndices(
    indicatorTableData,
    startDateFilter,
    endDateFilter,
    currentType,
    modelDetails,
  );

  // Optimized applyTimeFilter function
  const applyTimeFilter = useCallback(
    (data?: TableData) => {
      if (!data || !startDateFilter || !endDateFilter) return data;
      if (!timeFilterIndices) return data;

      const { columnStartIndex, columnEndIndex, activeDim } = timeFilterIndices;

      return {
        ...data,
        column_headers: [
          ...(activeDim > 0 ? data.column_headers.slice(0, activeDim) : []),
          ...data.column_headers.slice(columnStartIndex, columnEndIndex + 1),
        ],
        excel_data: data.excel_data.map((row) => {
          const newRow: Record<string, any> = {};
          if (activeDim > 0) {
            Object.entries(row)
              .slice(0, activeDim)
              .forEach(([key, value]) => {
                newRow[key] = value;
              });
          }
          Object.entries(row)
            .slice(columnStartIndex, columnEndIndex + 1)
            .forEach(([key, value]) => {
              newRow[key] = value;
            });
          return newRow;
        }),
      };
    },
    [timeFilterIndices, startDateFilter, endDateFilter],
  );

  // Memoize doesDimensionExist
  const doesDimensionExist = useMemo(() => {
    return indicatorTableData?.dimensions?.find(
      (dim: any) => Number(filteredItems[0]?.dimId) === Number(dim.id),
    );
  }, [indicatorTableData?.dimensions, filteredItems]);

  // Memoize the filtered items processing
  const processedFilteredItems = useMemo(() => {
    if (!filteredItems?.length) return [];

    return filteredItems.map((itemData: FilteredItem) => ({
      dimensionId: `${itemData.dimId}`,
      filterItems: itemData.filterItems
        .filter((item: FilterItem) => item?.status && item?.search)
        .map((item: FilterItem) => item.name.trim().toLowerCase()),
    }));
  }, [filteredItems]);

  // Memoize the table data transformation
  const transformedTableData = useMemo(() => {
    if (!indicatorTableData) return null;

    // If no filters or no dimension exists, just apply time filter if needed
    if (processedFilteredItems.length === 0 || !doesDimensionExist) {
      return startDateFilter && endDateFilter
        ? applyTimeFilter(indicatorTableData)
        : indicatorTableData;
    }

    // Create a deep copy of the table data
    const modifiedTable = JSON.parse(JSON.stringify(indicatorTableData));

    // Apply filters in a single pass
    modifiedTable.excel_data = modifiedTable.excel_data.filter((object: TableObject) => {
      return processedFilteredItems.every(({ dimensionId, filterItems }: ProcessedFilterItem) => {
        const dimensionValue = object[dimensionId]?.value?.trim()?.toLowerCase();
        return dimensionValue && filterItems.includes(dimensionValue);
      });
    });

    // Apply time filter if needed
    return startDateFilter && endDateFilter ? applyTimeFilter(modifiedTable) : modifiedTable;
  }, [
    indicatorTableData,
    processedFilteredItems,
    doesDimensionExist,
    startDateFilter,
    endDateFilter,
  ]);

  // Update table data when transformed data changes
  useEffect(() => {
    if (transformedTableData) {
      setTableData(transformedTableData);
    }
  }, [transformedTableData]);

  useEffect(() => {
    setTimeout(() => {
      setRender(show);
    }, 1000);
  }, [show]);

  const [customColumnWidths, setCustomColumnWidths] = useState<Record<string, number>>({});
  const [columns, setColumns] = useState<Column[]>([]);
  const showNonEditableColumns = useSelector(getShowNonEditableColumns);

  const hasAnyNonEditableValueColumns = useMemo(() => {
    if (!tableData?.column_headers?.length) return false;
    const scenarioDate = targetDate();
    return tableData.column_headers.some(
      (header: ColumnHeader) =>
        !header.is_dimension && isHeaderNonEditable(header, scenarioDate) === true,
    );
  }, [tableData?.column_headers, currentType, baseScenario?.start_date]);

  const visibleColumnHeaders = useMemo<ColumnHeader[]>(() => {
    if (!tableData?.column_headers?.length) return [];
    if (showNonEditableColumns) return tableData.column_headers;

    const scenarioDate = targetDate();
    return tableData.column_headers.filter(
      (header: ColumnHeader) => header.is_dimension || !isHeaderNonEditable(header, scenarioDate),
    );
  }, [tableData?.column_headers, showNonEditableColumns, currentType, baseScenario?.start_date]);

  const memorizedColumns = useMemo<Column[]>(() => {
    if (visibleColumnHeaders.length === 0 || !tableReference.current?.offsetWidth) return [];

    const columnWidth = calculateColumnWidth(
      visibleColumnHeaders.length,
      tableReference.current.offsetWidth,
      currentType === 'constant_by_year',
    );

    return visibleColumnHeaders.map((data: ColumnHeader): Column => {
      const columnId = data.is_dimension
        ? `${data.id}`
        : currentType === 'constant_by_year' || currentType === 'raw'
        ? `${timeDimension?.id} ${data.name}`
        : `${data.name}`;

      // Use custom width if available, otherwise use calculated width
      const width = customColumnWidths[columnId] || columnWidth;

      return {
        columnId,
        name: data.name,
        width,
        resizable: true,
        reorderable: !!data.is_dimension,
        timeDimId: timeDimension?.id,
      };
    });
  }, [
    tableData?.column_headers,
    tableReference.current?.offsetWidth,
    currentType,
    timeDimension?.id,
    customColumnWidths,
    visibleColumnHeaders,
  ]);

  useEffect(() => {
    if (memorizedColumns.length > 0) {
      setColumns(memorizedColumns);
    }
  }, [memorizedColumns]);

  const handleColumnResize = useCallback(
    (columnId: Id, width: number) => {
      const columnIdString = String(columnId);
      const MIN_WIDTH = currentType === 'constant_by_year' ? 120 : 100;
      const MAX_WIDTH = currentType === 'constant_by_year' ? 800 : 600;

      // Allow resizing up to the maximum width
      const boundedWidth = Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH);

      setCustomColumnWidths((previousWidths: Record<string, number>) => ({
        ...previousWidths,
        [columnIdString]: boundedWidth,
      }));

      setColumns((currentColumns) => {
        return currentColumns.map((col) =>
          col.columnId === columnIdString ? { ...col, width: boundedWidth } : col,
        );
      });
    },
    [currentType],
  );

  const rows = useMemo<Row[]>(() => {
    if (!tableData?.excel_data?.length || visibleColumnHeaders.length === 0) return [];
    const currencySymbol = getCurrencySymbol(modelDetails?.reporting_currency);
    const date = targetDate();
    const uniqueIds: Record<string, number> = {};
    let counter = 0;

    // Process unique IDs if needed
    const processedData =
      tableData.dimensions?.length > 0
        ? tableData.excel_data.map((element: TableItem, index: number) => {
            const firstId = tableData.column_headers[0]?.id;
            const newElement: TableItem = { ...element, id: String(index + 1) };

            if (firstId && element[String(firstId)]?.id !== undefined) {
              const elementId = element[String(firstId)].id;
              if (uniqueIds[elementId] === undefined) {
                uniqueIds[elementId] = counter;
                newElement[String(firstId)] = {
                  ...element[String(firstId)],
                  uniqueid: counter,
                };
                counter += 1;
              } else {
                newElement[String(firstId)] = {
                  ...element[String(firstId)],
                  uniqueid: uniqueIds[elementId],
                };
              }
            }
            return newElement;
          })
        : tableData.excel_data.map((element: TableItem, index: number) => ({
            ...element,
            id: String(index + 1),
          }));

    return createTableRows(processedData, visibleColumnHeaders, isPercentage, currencySymbol, date);
  }, [
    tableData,
    visibleColumnHeaders,
    isPercentage,
    baseScenario?.start_date,
    modelDetails?.reporting_currency,
  ]);

  useEffect(() => {
    if (memorizedColumns) {
      setColumns(memorizedColumns);
    }
  }, [memorizedColumns, render]);

  const processCellChanges = useCallback(
    (changes: CellChange[]) => {
      const organizedData: Record<string, Record<string, any>> = {};

      changes.forEach(({ rowId, columnId, newCell }) => {
        const value =
          newCell.text === ''
            ? '0'
            : isPercentage === 'percentage'
            ? Number(newCell.text) / 100
            : newCell.text;

        if (!organizedData[rowId]) {
          organizedData[rowId] = {};
        }
        organizedData[rowId][columnId] = value;
      });

      return Object.entries(organizedData).map(([rowId, rowData]) => ({
        ...rowData,
        rowId: Number(rowId),
      }));
    },
    [isPercentage],
  );

  const processDimensionData = useCallback(
    (result: Array<Record<string, any>>, tableRows: Row[]) => {
      return result.map((dataObject) => {
        const matchingRow = tableRows.find((row) => row.rowId === String(dataObject.rowId));
        if (matchingRow) {
          const dimensionData = matchingRow.cells
            .filter((cell) => cell.property === 'dimension')
            .reduce((accumulator, cell) => {
              if (cell.rowId) {
                accumulator[cell.rowId] = cell.text;
              }
              return accumulator;
            }, {} as Record<string, string>);

          const resultWithDimensions = { ...dataObject, ...dimensionData };
          // Remove rowId from the final data object, just like in the working version
          delete resultWithDimensions.rowId;
          return resultWithDimensions;
        }
        // Also remove rowId if no matching row found
        const { rowId, ...rest } = dataObject;
        return rest;
      });
    },
    [],
  );

  const createUpdatePayload = useCallback(
    (
      data: Array<Record<string, any>>,
      type: string,
      dimensions: Array<{ id: string | number; name: string }>,
      scenarioId: string | number,
      indicatorId: string,
    ): UpdatePayload => ({
      indicator_id: indicatorId,
      request_body: {
        bulk_input: true,
        format_excel_data: 'true',
        dimensions,
        data_values: data,
        type,
        scenario_id: scenarioId,
      },
    }),
    [],
  );

  const fetchIndicatorsData = useCallback(async () => {
    if (!plannerBlock?.dimensions?.length) return;

    const dimensionIds = plannerBlock.dimensions
      .filter((item: Dimension) => item.name !== 'Time')
      .map((item: Dimension) => item.id)
      .join(', ');

    dispatch(
      FetchBlockOutputs({
        blockId,
        params: {
          dim_id: dimensionIds,
          indicator_filter: 'all',
          scenario_id: baseScenario?.id,
        },
      }),
    );
  }, [plannerBlock?.dimensions, blockId, baseScenario?.id, dispatch]);

  const updateCellData = useCallback(
    async (changes: CellChange[]) => {
      if (!changes?.length || !indicatorTableData) return;

      const result = processCellChanges(changes);
      const processedData = processDimensionData(result, rows);

      try {
        if (indicatorTableData.type === 'constant_by_year' || indicatorTableData.type === 'raw') {
          const dimId = timeDimension?.id;
          const response = processedData.flatMap((change) => {
            const dimKeys = Object.keys(change).filter((key) => key.startsWith(String(dimId)));
            return dimKeys.map((dimKey) => {
              const [, year] = dimKey.split(' ');
              const { [dimKey]: unusedValue, ...rest } = change;
              const payload = {
                [String(dimId)]: year,
                value: change[dimKey],
                ...rest,
              };
              // Filter out keys with spaces (time dimension keys) to match working version
              const newPayload: Record<string, any> = {};
              Object.keys(payload).forEach((key) => {
                if (key.split(' ').length === 1) {
                  newPayload[key] = payload[key];
                }
              });
              return newPayload;
            });
          });

          const payload = createUpdatePayload(
            response,
            indicatorTableData.type,
            indicatorTableData.dimensions,
            baseScenario?.id,
            indicatorTableData.indicator_id,
          );

          await dispatch(UpdatePlangPageIndicatorTableData(payload));
        } else {
          const cleanData = processedData.map(({ rowId, ...rest }) => rest);
          const payload = createUpdatePayload(
            cleanData,
            indicatorTableData.type,
            indicatorTableData.dimensions,
            baseScenario?.id,
            indicatorTableData.indicator_id,
          );

          await dispatch(UpdatePlangPageIndicatorTableData(payload));
        }

        // BLOX-2110: always refresh the BLOX table/chart after an Input
        // Panel save. Previously this was gated on `show` (showPlanPage),
        // so when the user had the Input Panel expanded full-width (panel
        // only, no plan visible) the plan/BLOX view was never refetched —
        // values entered appeared to "not update into BLOX". The refetch
        // is cheap (same chart/table data the user will see on collapse)
        // and guarantees the update propagates end-to-end.
        refetchRequest();
        if (plannerBlock?.dimensions?.length > 0) {
          await fetchIndicatorsData();
        }
      } catch {
        // Intentionally swallow; callers refetch on success path.
      }
    },
    [
      indicatorTableData,
      rows,
      timeDimension?.id,
      baseScenario?.id,
      plannerBlock?.dimensions,
      processCellChanges,
      processDimensionData,
      createUpdatePayload,
      refetchRequest,
      fetchIndicatorsData,
    ],
  );

  const handleColumnsReorder = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (targetColumnId: Id, columnIds: Id[], dropPosition: DropPosition) => {
      const idsArray: string[] = [];
      indicatorTableData?.column_headers.forEach((dim: ColumnHeader) => {
        if (dim.is_dimension) {
          idsArray.push(String(dim.id));
        }
      });

      const targetIndex = idsArray.indexOf(String(targetColumnId));
      const columnIndex = idsArray.indexOf(String(columnIds[0]));

      idsArray.splice(targetIndex, 1, ...columnIds.map(String));

      if (targetIndex > columnIndex) {
        idsArray.splice(
          idsArray.indexOf(String(columnIds[0])),
          columnIds.length,
          String(targetColumnId),
        );
      } else {
        idsArray.splice(
          idsArray.lastIndexOf(String(columnIds[0])),
          columnIds.length,
          String(targetColumnId),
        );
      }

      const payload = {
        indicator_id: indicatorTableData?.indicator_id,
        request_body: {
          dimension_ids: idsArray,
          scenario_id: baseScenario?.id,
        },
      };

      await dispatch(updateIndicatorData(payload));
      dispatch(
        FetchPlangPageIndicatorTableData({
          indicator_id: indicatorTableData?.indicator_id,
          parameters: {
            scenario_id: baseScenario?.id,
            format_excel_data: true,
          },
        }),
      );
    },
    [indicatorTableData, baseScenario?.id, dispatch],
  );

  // useEffect(() => {
  //   if (currentDriver?.id) {
  //     dispatch(
  //       fetchIndicatorDetail({
  //         currentDriver,
  //         parameters: {
  //           dim_id: indicatorTableData?.column_headers[0].is_dimension
  //             ? indicatorTableData?.column_headers[0].id
  //             : null,
  //           scenario_id: baseScenario?.id,
  //         },
  //       }),
  //     );
  //   }
  // }, [indicatorTableData]);

  const cellTemplates = useCellTemplates();
  const gridProperties = useGridProperties(
    rows,
    columns,
    updateCellData,
    handleColumnResize,
    handleColumnsReorder,
    currentType,
    indicatorTableData?.dimensions?.length || 0,
  );
  const tableStyles = useTableStyles(
    Boolean(isBuilderMode),
    Boolean(isSmallDevice),
    Boolean(isMobile),
    rows?.length || 0,
    columns?.length || 0,
  );

  // // Optimize scroll position calculation
  // const [count, setCount] = useState(0);
  // useEffect(() => {
  //   if (tableReference.current && indicatorTableData?.column_headers && count === 0) {
  //     const container = tableReference.current;
  //     const dates = indicatorTableData.column_headers.map((data: ColumnHeader) => data.name);
  //     const formattedDate = `${startDateObject.toLocaleString('en-US', {
  //       month: 'short',
  //     })}-${startDateObject.getFullYear().toString().slice(-2)}`;
  //     const index = dates.indexOf(formattedDate);
  //     const totalWidth = Math.max(0, (index - 1) * 125);
  //     // Add a small delay to ensure ReactGrid is fully rendered
  //     setTimeout(() => {
  //       // Find the target column cell by data-cell-colidx
  //       const targetCell = container.querySelector(`[data-cell-colidx="${index}"]`);

  //       if (targetCell) {
  //         // Get the cell's position
  //         const cellRect = targetCell.getBoundingClientRect();
  //         const containerRect = container.getBoundingClientRect();
  //         const scrollPosition = cellRect.left - containerRect.left + container.scrollLeft;

  //         // Try to scroll the container to the target position
  //         container.scrollLeft = scrollPosition;

  //         // Also try scrolling the ReactGrid elements
  //         const reactGridElement = container.querySelector('.reactgrid');
  //         const reactGridContent = container.querySelector('.reactgrid-content');

  //         if (reactGridElement) {
  //           reactGridElement.scrollLeft = scrollPosition;
  //         }

  //         if (reactGridContent) {
  //           reactGridContent.scrollLeft = scrollPosition;
  //         }

  //         // Try scrolling the main scrollable container
  //         const mainScrollable = container.querySelector('.sm-scroll');
  //         if (mainScrollable) {
  //           mainScrollable.scrollLeft = scrollPosition;
  //         }
  //       } else {
  //         // Fallback to original width calculation
  //         const reactGridElement = container.querySelector('.reactgrid');
  //         const reactGridContent = container.querySelector('.reactgrid-content');

  //         if (reactGridElement) {
  //           reactGridElement.scrollLeft = totalWidth;
  //         } else if (reactGridContent) {
  //           reactGridContent.scrollLeft = totalWidth;
  //         }
  //       }
  //     }, 1000);

  //     setCount(1);
  //   }
  // }, [indicatorTableData?.column_headers, count]);
  return (
    <div style={{ width: '100%' }}>
      {hasAnyNonEditableValueColumns && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
          <button
            type='button'
            onClick={() => dispatch(toggleShowNonEditableColumns())}
            style={{
              border: '1px solid #CBD5E0',
              borderRadius: 6,
              padding: '6px 10px',
              background: 'transparent',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {showNonEditableColumns ? 'Hide actuals' : 'Show actuals'}
          </button>
        </div>
      )}

      <div
        style={{
          width: '100%',
          maxHeight: tableStyles.maxH,
          overflow: tableStyles.overflow === 'none' ? 'visible' : tableStyles.overflow,
          paddingRight: tableStyles.paddingRight,
        }}
        ref={tableReference}
        className='md-block-table-container sm-scroll'
      >
        {columns?.length > 0 && rows?.length > 0 && indicatorTableData && (
          <ReactGrid
            disableVirtualScrolling={false}
            {...gridProperties}
            enableColumnSelection
            enableRangeSelection
            enableFillHandle
            customCellTemplates={cellTemplates}
          />
        )}
      </div>
    </div>
  );
};

export default MultiDimensionTableOpz;
